import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useFormik } from 'formik'
import { requestPassword } from '../core/_requests'
import { checkForgotPasswordCode, passwordUpdate } from '../../../api'

const initialValues = {
  email: 'admin@demo.com',
  password: '',
  confirmPassword: ''
}

const passwordUpdateSchema = Yup.object().shape({
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Password confirmation is required'),
  email: Yup.string().email('Wrong email format').required('Email is required'),
})

export function PasswordUpdate() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams();
  const forgotPasswordCode = searchParams.get('fpCode');
  const [setupPassword, setSetupPassword] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState)
  }

  const getAccessDetail = async () => {
    try {
      const accessDetail = await checkForgotPasswordCode(forgotPasswordCode || '')
      if (!accessDetail) setSetupPassword(accessDetail)

    } catch (error: any) {
      navigate('/error/404')
    }

  }

  useEffect(() => {
    getAccessDetail()
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: passwordUpdateSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        const passwordUpdateResult = passwordUpdate(forgotPasswordCode || '', formik.values.password)
        setFormSuccess(true)
        setLoading(false)
      }, 1000)
    },
  })

  const title =  'Reset your password'.toTranslatedDisplay()

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-gray-900 fw-bolder mb-3'>{title}</h1>
        {formSuccess ?
          <>
            <div className='mb-10 bg-light-info p-8 rounded'>
              <div className='text-info'>{'Password reset!'.toTranslatedDisplay()}</div>
            </div>
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              <button onClick={()=>{navigate('/auth/login')}} className='btn btn-primary'>
                  {'Login'.toTranslatedDisplay()}
              </button>
            </div>
          </> :
          <>

            {setupPassword && (
              <>
                <div className='text-gray-500 fw-semibold fs-6'>{'Enter your new password'.toTranslatedDisplay()}</div>

                {/* Password Field */}
                <div className='fv-row mb-8' data-kt-password-meter='true'>
                  <div className='mb-1'>
                    <label className='form-label fw-bolder text-gray-900 fs-6'>{'Password'.toTranslatedDisplay()}</label>
                    <div className='position-relative mb-3'>
                      <div className="input-group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={'Password'.toTranslatedDisplay()}
                          autoComplete='off'
                          {...formik.getFieldProps('password')}
                          className={clsx(
                            'form-control bg-transparent',
                            {
                              'is-invalid': formik.touched.password && formik.errors.password,
                            },
                            {
                              'is-valid': formik.touched.password && !formik.errors.password,
                            }
                          )}
                        />
                        <span className="input-group-text" onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                          {showPassword ? (
                            <i className="bi bi-eye-slash"></i>
                          ) : (
                            <i className="bi bi-eye"></i>
                          )}
                        </span>
                      </div>
                      {formik.touched.password && formik.errors.password && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.password?.toTranslatedDisplay()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='text-muted'>{'Use 8 or more characters with a mix of letters, numbers & symbols.'.toTranslatedDisplay()}</div>
                </div>

                {/* Confirm Password Field */}
                <div className='fv-row mb-5'>
                  <label className='form-label fw-bolder text-gray-900 fs-6'>{'Confirm Password'.toTranslatedDisplay()}</label>
                  <div className="input-group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={'Password confirmation'.toTranslatedDisplay()}
                      autoComplete='off'
                      {...formik.getFieldProps('confirmPassword')}
                      className={clsx(
                        'form-control bg-transparent',
                        {
                          'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword,
                        },
                        {
                          'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
                        }
                      )}
                    />
                    <span className="input-group-text" onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                      {showPassword ? (
                        <i className="bi bi-eye-slash"></i>
                      ) : (
                        <i className="bi bi-eye"></i>
                      )}
                    </span>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.confirmPassword?.toTranslatedDisplay()}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                  <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>

                    {loading ? (
                      <span className='indicator-progress'>
                        {'Please wait...'.toTranslatedDisplay()}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) :
                      <span className='indicator-label'>{'Reset Password'.toTranslatedDisplay()}</span>
                    }
                  </button>
                  <Link to='/auth/login'>
                    <button
                      type='button'
                      id='kt_login_password_reset_form_cancel_button'
                      className='btn btn-light'
                      disabled={formik.isSubmitting || !formik.isValid}
                    >{'Cancel'.toTranslatedDisplay()}</button>
                  </Link>
                </div>
              </>
            )}
          </>


        }



      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {'Sorry, looks like there are some errors detected, please try again.'.toTranslatedDisplay()}
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>{'Sent password reset. Please check your email'.toTranslatedDisplay()}</div>
        </div>
      )}


    </form>
  )
}
